import React, {useMemo} from 'react';
import './ProfilePackageAccordion.css';
import {useAppSelector} from "../../../redux/hooks";
import {bookingsByProfilePackageIdSelector} from "../../../redux/bookings";
import {IonAccordion, IonButton, IonChip, IonItem, IonLabel, IonText} from "@ionic/react";
import {Booking, Company, ProfilePackage} from "../../../models";
import {Ages} from "../../../utils/ages";
import {freezeCoversBookingWindow, isTsWithinFreeze} from "../../../utils/frozen";

interface ProfilePackageAccordionProps {
    profilePackage: ProfilePackage;
    frozenUntil?: string | null;
    useSelfBooking: boolean;
    useClasses: boolean;
    setSelectedBooking: (booking?: Booking) => void;
    company: Company;
}

const ProfilePackageAccordion: React.FC<ProfilePackageAccordionProps> = (props) => {
    const {setSelectedBooking, profilePackage, frozenUntil, useClasses, company} = props;
    const bookingsByProfilePackageId = useAppSelector((state) => bookingsByProfilePackageIdSelector(state, profilePackage.id))
    const bookings = useMemo(() => {
        return bookingsByProfilePackageId && bookingsByProfilePackageId.filter((booking) => new Ages(booking.expiredTimestamp).gte(new Ages().subDays()))
    }, [bookingsByProfilePackageId])

    // While the profile is frozen, a recurring package's weekly sessions are all
    // suspended; a dated (non-recurring) booking is suspended only when its date
    // falls inside the freeze window.
    const isBookingFrozen = (booking: Booking): boolean =>
        profilePackage.isRecurring
            ? !!frozenUntil
            : isTsWithinFreeze(frozenUntil, booking.startedTimestamp);

    // When the freeze outlasts every date the slots calendar can offer, the
    // Manage/View entry point would only show a wall of dead dates — say
    // "Frozen" instead of pretending there is something to manage.
    const isManageFrozen = !profilePackage.isEvent &&
        freezeCoversBookingWindow(frozenUntil, profilePackage.weeksAvailableFuture || 3);

    const hasBookings = bookings && bookings.length > 0;
    const noBookingsMessage = <IonText><p className="ion-padding-top">No upcoming bookings</p></IonText>;
    if (bookingsByProfilePackageId === undefined)return noBookingsMessage

    return (
        <IonAccordion className={'profile-package-accordion'} value={`profile-package-accordion-${profilePackage.id}`}>
            <IonItem slot="header" color="light">
                <IonLabel>
                    <IonText>{profilePackage.name}</IonText>
                    <IonText className={"profile-package-info-holder"} color="primary">
                        {useClasses && !profilePackage.isEvent && !!profilePackage.bookingLimit && profilePackage.bookingLimit > 0 &&
                            (!profilePackage.lessonLimit || profilePackage.bookingLimit < profilePackage.lessonLimit) &&
                            <p>Weekly Limit: {profilePackage.bookingLimit}</p>
                        }
                        {useClasses && !!profilePackage.lessonLimit && profilePackage.lessonLimit > 0 &&
                            <p>Limit: {profilePackage.usedLessons ? `${profilePackage.usedLessons}/` : ''}{profilePackage.lessonLimit}</p>
                        }
                        {useClasses && !!profilePackage.monthlyBookingsDisplay &&
                            <p>{profilePackage.monthlyBookingsDisplay}</p>
                        }
                        {useClasses && profilePackage.lessonEndTs &&
                            <p>{profilePackage.lessonStartTs && profilePackage.lessonStartTs === profilePackage.lessonEndTs 
                                ? (profilePackage.isEvent ? 'Event Date: ' : `${company.langLesson || 'Session'} Date: `)
                                : 'End Date: '
                            }{new Ages(profilePackage.lessonEndTs).toFormat()}</p>
                        }
                    </IonText>
                </IonLabel>
                {useClasses && (
                    (isManageFrozen && <p className="profile-package-frozen-label">Frozen</p>) ||
                    ((profilePackage.canViewBookings || profilePackage.canManageBookings) &&
                    <IonButton
                        routerLink={
                            profilePackage.isEvent && profilePackage.eventId
                                ? `/events/${profilePackage.eventId}`
                                : `/slots/${profilePackage.id}`
                        }
                        color={"primary"}
                    >
                        {profilePackage.isEvent
                            ? "View"
                            : ((profilePackage.canManageBookings && "Manage...") || "View")
                        }
                    </IonButton>) || <p>{profilePackage.actionStatus}</p>)
                }

            </IonItem>
            <div className="ion-padding-horizontal ion-padding-bottom" slot="content">
                {useClasses && !hasBookings && noBookingsMessage}
                {useClasses && bookings.sort((a, b) => (a.startedTimestamp > b.startedTimestamp ? 1 : -1)).map((booking) => (
                    booking.slot ?
                        <IonChip className={`profile-package-booking-chip ion-text-center${isBookingFrozen(booking) ? ' profile-package-booking-chip-frozen' : ''}`}
                                 key={`profile-package-booking-${booking.id}`}
                                 onClick={() => {setSelectedBooking(booking)}}
                        >
                            <span className="profile-package-booking-chip-schedule">
                                {booking.slot.dowAbbreviation} {!profilePackage.isRecurring && (new Ages(booking.startedTimestamp).toFormat("dd mmm"))} @ {booking.slot.startTimeDisplay} - {booking.slot.endTimeDisplay}
                                <br/>
                                {booking.slot.facility.name}
                            </span>
                            {isBookingFrozen(booking) && <span className="profile-package-booking-chip-frozen-label">Frozen</span>}
                        </IonChip>
                        : <></>
                ))}
            </div>
        </IonAccordion>
    );
};

export default ProfilePackageAccordion;
